import { LanguageCode } from '@app/shared/utils/enums/language-code.enum';
import { IconName } from '@app/shared/utils/icon/icons';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  signal,
  ChangeDetectionStrategy,
  OnDestroy,
  SimpleChanges,
  OnChanges,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { environment } from '@env/environment';
import { BehaviorSubject } from 'rxjs';
import { VetFacade } from '@app/store/vet';
import { VERTICAL_TOP_TO_BOTTOM_LANGUAGES } from '@app/core/services/utils/localization/localization';
import { map } from 'rxjs/operators';
import { Constants } from '@app/shared/utils';
import { CommonModule } from '@angular/common';

import { IconComponent } from '@app/shared/utils/icon/icon.component';
import { StepperComponent } from '../stepper/stepper.component';
import { CoreFacade } from '@app/store/core/core.facade';

const MARS_FOOTER = 'mars-footer';
const APP_FOOTER = 'app-footer';

export enum ContentWrapperSizes {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

@Component({
  selector: 'app-page-wrapper',
  templateUrl: './page-wrapper.component.html',
  styleUrls: ['./page-wrapper.component.scss'],
  imports: [CommonModule, IconComponent, StepperComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PageWrapperComponent implements OnInit, OnDestroy, OnChanges {
  @Input() size = ContentWrapperSizes.SMALL;
  @Input() withLeftPanel: { show: boolean; opened: boolean; alwaysOpened?: boolean } = { show: false, opened: false, alwaysOpened: false };
  @Input() withRightPanel: { show: boolean; opened: boolean; alwaysOpened?: boolean } = { show: false, opened: false, alwaysOpened: false };
  @Input() withFooterMargin = false;
  @Input() leftPanelLabel = translateKey('action_display-profile');
  @Input() rightPanelLabel = translateKey('selected-products');
  @Input() withAside = false;
  @Input() fullWidth = false;
  @Input() withLegacyProgressBar = false;
  @Output() panelOpenedEvent = new EventEmitter<boolean>();

  public isStandalone = environment.isStandalone;
  public contentWrapperSizes = ContentWrapperSizes;
  public iconNames = IconName;
  public leftPanelOpened = signal<boolean>(false);
  public rightPanelOpened = signal<boolean>(false);
  public hasScrolledToMarsFooter$ = new BehaviorSubject(false);
  public isVerticalTopToBottomLanguage$ = this.vetFacade.preferredLanguage$.pipe(
    map((language) => VERTICAL_TOP_TO_BOTTOM_LANGUAGES.includes(language as LanguageCode)),
  );
  showAppFooter$ = this.vetFacade.clinic$.pipe(
    map((clinic) => !this.isStandalone && Constants.COUNTRIES_WITH_FOOTER.some((country) => clinic?.companyAddress?.country === country)),
  );

  public stepperItems$ = this.coreFacade.stepperItems$;

  constructor(
    private readonly vetFacade: VetFacade,
    private readonly coreFacade: CoreFacade,
  ) {}

  ngOnInit(): void {
    // use IntersectionObserver to detect if user scrolled to bottom and reached the mars
    // https://css-tricks.com/styling-based-on-scroll-position/
    // https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserverEntry/isIntersecting
    if (
      'IntersectionObserver' in window &&
      'IntersectionObserverEntry' in window &&
      'intersectionRatio' in window.IntersectionObserverEntry.prototype
    ) {
      const observer = new IntersectionObserver((entries) => {
        this.hasScrolledToMarsFooter$.next(!!entries[0]?.isIntersecting);
      });
      const marsFooter = document.querySelector('#mars-footer-panel');
      if (marsFooter) observer.observe(marsFooter);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.withLeftPanel?.currentValue.show) {
      const leftPanelClass = `${MARS_FOOTER}--withLeftPanel`;
      document.querySelector(`#${MARS_FOOTER}-panel`)?.classList.add(leftPanelClass);
    }
    if (changes.withRightPanel?.currentValue.show) {
      const rightPanelClass = `${MARS_FOOTER}--withRightPanel`;
      document.querySelector(`#${MARS_FOOTER}-panel`)?.classList.add(rightPanelClass);
    }

    if (changes.withLeftPanel?.currentValue.show && changes.withLeftPanel?.currentValue.opened) {
      this.onToggleLeftPanel();
    }
    if (changes.withRightPanel?.currentValue.show && changes.withRightPanel?.currentValue.opened) {
      this.onToggleRightPanel();
    }
  }

  ngOnDestroy(): void {
    this.hidePanels();
  }

  onToggleLeftPanel(): void {
    this.leftPanelOpened.update((value) => !value);
    const panelOpened: boolean = this.leftPanelOpened();
    this.toggleFooter(panelOpened, true, MARS_FOOTER);
    this.toggleFooter(panelOpened, true, APP_FOOTER);
    this.panelOpenedEvent.emit(panelOpened);
  }

  onToggleRightPanel(): void {
    this.rightPanelOpened.update((value) => !value);
    const panelOpened: boolean = this.rightPanelOpened();
    this.toggleFooter(panelOpened, false, MARS_FOOTER);
    this.toggleFooter(panelOpened, false, APP_FOOTER);
    this.panelOpenedEvent.emit(panelOpened);
  }

  private toggleFooter(panelOpened: boolean, isLeftPanel: boolean, footerPrefix: string): void {
    const leftPanelClass = `${footerPrefix}--withLeftPanelOpened`;
    const rightPanelClass = `${footerPrefix}--withRightPanelOpened`;
    const cookiePanelClass = 'cookie-consent--withRightPanelOpened';
    if (panelOpened) {
      document.querySelector(`#${footerPrefix}-panel`)?.classList.add(isLeftPanel ? leftPanelClass : rightPanelClass);
    } else {
      document.querySelector(`#${footerPrefix}-panel`)?.classList.remove(isLeftPanel ? leftPanelClass : rightPanelClass);
    }

    if (panelOpened && !isLeftPanel) {
      document.querySelector('#cookie-consent')?.classList.add(cookiePanelClass);
    } else {
      document.querySelector('#cookie-consent')?.classList.remove(cookiePanelClass);
    }
  }

  private hidePanels(): void {
    this.leftPanelOpened.set(false);
    this.rightPanelOpened.set(false);
    this.toggleFooter(false, true, MARS_FOOTER);
    this.toggleFooter(false, true, APP_FOOTER);
    this.toggleFooter(false, false, MARS_FOOTER);
    this.toggleFooter(false, false, APP_FOOTER);
  }
}
